import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Folder, MessageSquare, MoreHorizontal, X, ChevronRight, ChevronDown } from 'lucide-react';
import { Button } from './Button';
import { Input } from './Input';
import { ScrollArea } from './ScrollArea';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './Collapsible';
import { Chat, Project } from '../types';
interface ProjectsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  projects: Project[];
  chats: Chat[];
  activeChatId: string | null;
  onSelectChat: (id: string) => void;
}
export function ProjectsPanel({
  isOpen,
  onClose,
  projects,
  chats,
  activeChatId,
  onSelectChat
}: ProjectsPanelProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [openProjects, setOpenProjects] = useState<Record<string, boolean>>({});
  const toggleProject = (id: string) => {
    setOpenProjects(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  const filteredProjects = projects.filter(project => project.name.toLowerCase().includes(searchQuery.toLowerCase()));
  const getProjectChats = (projectId: string) => chats.filter(c => c.projectId === projectId);
  return <AnimatePresence data-id="element-102">
      {isOpen && <motion.div initial={{
      width: 0,
      opacity: 0
    }} animate={{
      width: 280,
      opacity: 1
    }} exit={{
      width: 0,
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: 'easeInOut'
    }} className="h-full bg-[#1a1e2e] border-r border-white/5 flex flex-col flex-shrink-0 overflow-hidden" data-id="element-103">
          <div className="w-[280px] h-full flex flex-col" data-id="element-104">
            <div className="p-4 flex items-center justify-between border-b border-white/5" data-id="element-105">
              <h2 className="text-sm font-semibold text-slate-200 tracking-wide" data-id="element-106">
                Projects
              </h2>
              <Button variant="ghost" size="icon" onClick={onClose} className="h-6 w-6 text-[#6b7194] hover:text-white" data-id="element-107">
                <X className="h-4 w-4" data-id="element-108" />
              </Button>
            </div>

            <ScrollArea className="flex-1 px-2 py-4" data-id="element-109">
              <div className="px-2 mb-4" data-id="element-110">
                <div className="relative" data-id="element-111">
                  <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-[#6b7194]" data-id="element-112" />
                  <Input placeholder="Search projects..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="h-8 pl-8 bg-black/20 border-transparent focus-visible:ring-1 focus-visible:ring-[#4a4f6a] text-xs placeholder:text-[#6b7194]" data-id="element-113" />
                </div>
              </div>

              <div className="space-y-0.5" data-id="element-114">
                {filteredProjects.map(project => {
              const projectChats = getProjectChats(project.id);
              const isExpanded = openProjects[project.id] ?? false;
              return <Collapsible key={project.id} open={isExpanded} onOpenChange={() => toggleProject(project.id)} data-id="element-115">
                      <div className="group relative px-2" data-id="element-116">
                        <CollapsibleTrigger asChild data-id="element-117">
                          <Button variant="ghost" className={`w-full justify-between h-9 px-2 font-normal ${isExpanded ? 'bg-white/5 text-slate-200' : 'text-[#8b8fa8] hover:bg-white/5 hover:text-slate-200'}`} data-id="element-118">
                            <div className="flex items-center min-w-0" data-id="element-119">
                              {isExpanded ? <ChevronDown className="h-3.5 w-3.5 mr-1.5 flex-shrink-0 text-[#6b7194]" data-id="element-120" /> : <ChevronRight className="h-3.5 w-3.5 mr-1.5 flex-shrink-0 text-[#6b7194]" data-id="element-121" />}
                              <Folder className="h-4 w-4 mr-2 flex-shrink-0 text-[#6c5ce7]" data-id="element-122" />
                              <span className="truncate text-sm" data-id="element-123">
                                {project.name}
                              </span>
                            </div>
                            <span className="text-[11px] text-[#6b7194] flex-shrink-0 ml-2" data-id="element-124">
                              {projectChats.length}
                            </span>
                          </Button>
                        </CollapsibleTrigger>
                      </div>

                      <CollapsibleContent className="pl-4 space-y-0.5 mt-0.5" data-id="element-125">
                        {projectChats.map(chat => <div key={chat.id} className="px-2" data-id="element-126">
                            <Button variant="ghost" onClick={() => onSelectChat(chat.id)} className={`w-full justify-start h-8 px-2 font-normal ${activeChatId === chat.id ? 'bg-[#252a42] text-white' : 'text-[#8b8fa8] hover:bg-white/5 hover:text-slate-200'}`} data-id="element-127">
                              <MessageSquare className={`h-3.5 w-3.5 mr-2 flex-shrink-0 ${activeChatId === chat.id ? 'text-emerald-400' : 'opacity-70'}`} data-id="element-128" />
                              <span className="truncate text-sm" data-id="element-129">
                                {chat.title}
                              </span>
                            </Button>
                          </div>)}
                      </CollapsibleContent>
                    </Collapsible>;
            })}
              </div>

              {filteredProjects.length === 0 && <div className="px-4 py-8 text-center text-[#6b7194] text-sm" data-id="element-130">
                  No projects found
                </div>}
            </ScrollArea>
          </div>
        </motion.div>}
    </AnimatePresence>;
}